import React, { useState } from 'react';
import { Checkbox } from 'antd';
import SearchInput from '@/components/SearchInput';
import { ChevronDownSmallIcon, ChevronUpIcon } from '@/assets/icons/icons';
import { EventTypeGroup } from '../../types';
import { getAllLeafEventTypeIds } from '../../utils/filterEvents/filterEvents';

interface EventsFilterDropdownProps {
  groups: EventTypeGroup[];
  selectedTypeIds: Set<string>;
  onChange: (next: Set<string>) => void;
  onSearchChange: (search: string) => void;
}

const EventsFilterDropdown: React.FC<EventsFilterDropdownProps> = ({
  groups,
  selectedTypeIds,
  onChange,
  onSearchChange,
}) => {
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    new Set()
  );

  // `groups` already matches the current search — the backend filters it,
  // so there's no local narrowing left to do here.
  const allIds = getAllLeafEventTypeIds(groups);
  const selectedCount = allIds.filter((id) => selectedTypeIds.has(id)).length;
  const allChecked = allIds.length > 0 && selectedCount === allIds.length;
  const someChecked = selectedCount > 0 && !allChecked;

  const setIds = (ids: string[], checked: boolean) => {
    const next = new Set(selectedTypeIds);
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
    onChange(next);
  };

  const toggleExpanded = (groupId: string) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  return (
    <div
      className='activity-feed-dropdown-card activity-events-dropdown'
      onClick={(e) => e.stopPropagation()}
    >
      <SearchInput
        placeholder='Search events'
        width='100%'
        onChange={onSearchChange}
        onClear={() => onSearchChange('')}
      />
      <div className='activity-feed-dropdown-list'>
        <div className='activity-feed-dropdown-item'>
          <Checkbox
            className='checkbox-sm'
            checked={allChecked}
            indeterminate={someChecked}
            onChange={(e) => setIds(allIds, e.target.checked)}
          >
            All events
          </Checkbox>
        </div>

        {groups.map((group) => {
          if (!group.children) {
            return (
              <div key={group.id} className='activity-feed-dropdown-item'>
                <Checkbox
                  className='checkbox-sm'
                  checked={selectedTypeIds.has(group.id)}
                  onChange={(e) => setIds([group.id], e.target.checked)}
                >
                  {group.label}
                </Checkbox>
              </div>
            );
          }

          const childIds = group.children.map((child) => child.id);
          const groupSelectedCount = childIds.filter((id) =>
            selectedTypeIds.has(id)
          ).length;
          const groupChecked =
            childIds.length > 0 && groupSelectedCount === childIds.length;
          const groupIndeterminate = groupSelectedCount > 0 && !groupChecked;
          const expanded = expandedGroupIds.has(group.id);

          return (
            <div key={group.id} className='activity-feed-dropdown-group'>
              <div className='activity-feed-dropdown-item activity-feed-dropdown-group-header'>
                <Checkbox
                  className='checkbox-sm'
                  checked={groupChecked}
                  indeterminate={groupIndeterminate}
                  onChange={(e) => setIds(childIds, e.target.checked)}
                >
                  {group.label}
                </Checkbox>
                <span
                  className='activity-feed-dropdown-chevron'
                  role='button'
                  aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.label}`}
                  onClick={() => toggleExpanded(group.id)}
                >
                  {expanded ? (
                    <ChevronUpIcon size={12} color='var(--text-secondary)' />
                  ) : (
                    <ChevronDownSmallIcon
                      size={12}
                      color='var(--text-secondary)'
                    />
                  )}
                </span>
              </div>
              {expanded &&
                group.children.map((child) => (
                  <div
                    key={child.id}
                    className='activity-feed-dropdown-item activity-feed-dropdown-child-item'
                  >
                    <Checkbox
                      className='checkbox-sm'
                      checked={selectedTypeIds.has(child.id)}
                      onChange={(e) => setIds([child.id], e.target.checked)}
                    >
                      {child.label}
                    </Checkbox>
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventsFilterDropdown;
